
import requestAPI from '@/logic/requestBeApi';
import AssessorOnboardingBasics from '@/views/AssessorApp/Onboarding/AssessorOnboardingBasics.vue';
import AssessorOnboardingExpertise from '@/views/AssessorApp/Onboarding/AssessorOnboardingExpertise.vue';
import AssessorOnboardingCompanyDetails from '@/views/AssessorApp/Onboarding/AssessorOnboardingCompanyDetails.vue';
import AssessorOnboardingBusinessConditions from '@/views/AssessorApp/Onboarding/AssessorOnboardingBusinessConditions.vue';
import AssessorCareer from './Optimize/AssessorCareer.vue';
import AssessorEducation from './Optimize/AssessorEducation.vue';
import AssessorAvatarUploader from '@/components/AssessorAvatarUploader.vue';
import EazyHeading from '@/components/EazyHeading.vue';
import router from '@/router';
import store from '@/store';
import { authLogOut } from '@/logic/auth';
import BlueCheckbox from '@/components/BlueCheckbox.vue';
import helpToDeleteAccount from '@/logic/helpers/helpToDeleteAccount';
import UserProfileCompletionSteps from '@/components/UserProfileCompletionSteps.vue';
import newsletter from '@/logic/services/newsletter';

export default {
  name: 'AssessorProfile',

  components: {
    AssessorOnboardingBasics,
    AssessorOnboardingExpertise,
    AssessorOnboardingCompanyDetails,
    AssessorOnboardingBusinessConditions,
    AssessorCareer,
    AssessorEducation,
    AssessorAvatarUploader,
    EazyHeading,
    BlueCheckbox,
    UserProfileCompletionSteps,
  },

  data(): Record<string, unknown> {
    return {
      router,
      showNotFullyOnboardedAlert: false,
      showTitleSelect: false,
      disableFutureDates: {
        disabledDate(time: { getTime: () => number }) {
          return time.getTime() > Date.now();
        },
      },
      contactDetailsId: '',
      verified: false,
      onboardedFully: false,
      processingUserDataRequest: false,
      basics: {
        address: {},
      },
      email: '',
      assessorExpertises: {},
      company: {},
      profileCompletionPercentage: 0,
      availableForAssessments: false,
      profileChecklistData: {},
      profileChecklist: {},
      missingSectionsText: '',
      hasEveryTypeOfExpertise: undefined,
      requestError: false,
      showNumberOfAssessedSection: false,
      showRanking: false,
      userSubmittedOnboardingBasics: false,
      focusOnProgressCircle: this.checkIffocusOnProgressCircle(),
      showOverlayToFocusOnAvatar: false,
      deletingAccount: false,
      canShowCompletionSteps: false,
      subscribedToNewsletter: false,
      upgradedToEazyPlus: false,
      removedEazyPlus: false,
    };
  },

  computed: {
    isAssessorPlus(): boolean {
      return localStorage.getItem('roleType') === 'assessorPlus';
    },

    allowedToMatch: {
      get(): boolean {
        return store.state.allowedToMatch;
      },

      set(newVal: boolean): boolean {
        return newVal;
      },
    },

    lackOfFullAccess(): boolean {
      return (
        !this.allowedToMatch || !this.verified
        // || !this.onboardedFully
      );
    },
  },

  watch: {
    profileCompletionPercentage(): void {
      if (this.profileCompletionPercentage === 100) {
        this.allowedToMatch = true;
        this.onboardedFully = true;
      }
    },
  },

  beforeCreate(): void {
    store.dispatch('meta/handleHeavyPageVisit');
  },

  async created(): Promise<void> {
    await this.getAssessorData();
    await this.getProfileCompletionPercentage(this.email);
    this.checkProfileCompletion();

    this.canShowCompletionSteps = true;
  },

  mounted(): void {
    this.checkIfShouldFocusOnContractDets();
  },

  methods: {
    async getProfileCompletionPercentage(email: string): Promise<void> {
      if (!email) return;

      const resp = await requestAPI({
        url: `/api/v1/onboard/state/${email}`,
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();
        this.profileCompletionPercentage = data.onboarding_percentage;
      }
    },

    async getAssessorData(updateInTheBackground = false): Promise<void> {
      if (!updateInTheBackground) this.processingUserDataRequest = true;

      const resp = await requestAPI({
        url: '/api/v1/assessors/me',
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();
        this.basics = data.contact_details || {};

        if (Object.keys(this.basics).length) {
          this.email = data.email;
          this.availableForAssessments = data.available;
          this.verified = data.verified;
          this.onboardedFully = data.onboarded_fully;
          this.allowedToMatch = data.allowed_to_match;
          this.subscribedToNewsletter = data.newsletter;

          store.commit('setUserId', data.id);
          store.commit('setAccountVerified', data.verified);
          localStorage.setItem('accountVerified', data.verified);
          store.commit('setAssessorWorkingHours', data.working_hours);
          localStorage.setItem('assessorWorkingHours', data.working_hours);
        }

        if (!Object.keys(this.assessorExpertises).length) {
          this.assessorExpertises = {
            assessmentTypes: data.expertises.filter(
              (exp: { expertise_type: { slug: string } }) =>
                exp.expertise_type.slug === 'assessment-types',
            ),
            tools: data.expertises.filter(
              (exp: { expertise_type: { slug: string } }) => exp.expertise_type.slug === 'tools',
            ),
            functions: data.expertises.filter(
              (exp: { expertise_type: { slug: string } }) =>
                exp.expertise_type.slug === 'functions',
            ),
            levels: data.expertises.filter(
              (exp: { expertise_type: { slug: string } }) => exp.expertise_type.slug === 'levels',
            ),
            countriesPerformedIn: data.expertises.filter(
              (exp: { expertise_type: { slug: string } }) =>
                exp.expertise_type.slug === 'countries-performed-in',
            ),
            nationalitiesAssessed: data.expertises.filter(
              (exp: { expertise_type: { slug: string } }) =>
                exp.expertise_type.slug === 'nationalities-assessed',
            ),
            languages: data.expertises.filter(
              (exp: { expertise_type: { slug: string } }) =>
                exp.expertise_type.slug === 'languages',
            ),
            industries: data.expertises.filter(
              (exp: { expertise_type: { slug: string } }) =>
                exp.expertise_type.slug === 'industries',
            ),
            companies: data.expertises.filter(
              (exp: { expertise_type: { slug: string } }) =>
                exp.expertise_type.slug === 'companies',
            ),
          };
        }

        this.company = data.company;

        this.profileChecklistData = {
          personalInformation: data.contact_details || {},
          contractDetails: data.company || {},
          expertises: data.expertises,
          careers: data.careers,
          education: data.education,
          businessConditions: [data.working_days, data.working_hours, data.distance_of_work],
        };
      } else if (resp.status !== 401) {
        this.$message({
          message: this.$t('someErrorOccurred'),
          type: 'error',
        });
      }

      this.processingUserDataRequest = false;
    },

    async runGetProfileCompletionProcess(): Promise<void> {
      const email = localStorage.getItem('email');
      const focusDuration = 12000;

      await this.getAssessorData(true);
      this.checkProfileCompletion();

      if (this.focusOnProgressCircle) {
        this.showOverlayToFocusOnAvatar = true;

        setTimeout(() => {
          this.showOverlayToFocusOnAvatar = false;
        }, focusDuration);

        window.scrollTo(0, 0);

        this.$notify.success({
          title: this.$t('profileTipTitle'),
          message: this.$t('profileTipDesc'),
          duration: focusDuration,
          offset: 120,
        });

        this.getProfileCompletionPercentage(email);
        localStorage.setItem('userAlreadyGotCompletionProfileTip', 'true');
        this.focusOnProgressCircle = false;
      } else {
        this.getProfileCompletionPercentage(email);
      }
    },

    async submitAvailability(): Promise<void> {
      const resp = await requestAPI({
        method: 'PATCH',
        contentType: 'application/merge-patch+json',
        url: '/api/v1/assessors/me/available',
        payload: { available: this.availableForAssessments },
      });

      if (resp.ok) this.$message.success(this.$t('yourInformationHasBeenUpdated'));
      else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));
    },

    logOut(): void {
      authLogOut();
    },

    checkProfileCompletion(): void {
      delete this.profileChecklistData.personalInformation?.initials;

      const personalInfo = Object.values(this.profileChecklistData.personalInformation).some(
        (value) => value,
      )
        ? ''
        : `• ${this.$t('basicInformation')}`;

      let contractDetails = Object.keys(this.profileChecklistData.contractDetails).length
        ? ''
        : ` • ${this.$t('contractDetails')}`;

      if (!contractDetails) {
        const hasAllBankInfo = Object.values(this.profileChecklistData.contractDetails.bank).every(
          (value) => value,
        );
        //const hasRegNumber = this.profileChecklistData.contractDetails.registration_number ? true : false;
        //const hasPhoneNumber = this.profileChecklistData.contractDetails.phones.length ? true : false;
        //const hasAptNumber = this.profileChecklistData.contractDetails.addresses[0].apartment_number ? true : false;
        const allConditionsAbove = [hasAllBankInfo];
        const someDetsMissing = ` • ${this.$t('contractDetails')}`;

        contractDetails = allConditionsAbove.every((condition) => condition) ? '' : someDetsMissing;
      }

      const assessmentExp = this.hasEveryTypeOfExpertise
        ? ''
        : ` • ${this.$t('assessmentExpertise')}`;
      const businessConds = this.profileChecklistData.businessConditions.every(
        (value: unknown) => value,
      )
        ? ''
        : ` • ${this.$t('businessConditions')}`;
      const career = this.profileChecklistData.careers.length ? '' : ` • ${this.$t('career')}`;
      const education = this.profileChecklistData.education.length
        ? ''
        : ` • ${this.$t('education')}`;

      this.profileChecklist = {
        personalInformation: !personalInfo,
        contractDetails: !contractDetails,
        expertises: !assessmentExp,
        careers: !career,
        education: !education,
        businessConditions: !businessConds,
      };

      this.missingSectionsText = `${this.$t(
        'missingInfoMessage',
      )} ${personalInfo}${assessmentExp}${businessConds}${career}${education}${contractDetails}`;
    },

    showMissingInformation(): void {
      this.$alert(this.missingSectionsText, this.$t('missingInfo'), {
        confirmButtonText: 'OK',
        callback: () => {
          this.$message.info({ message: this.$t('checkNonMandatoryInputs'), duration: 5000 });
        },
      });
    },

    askIfSureToDelete(): void {
      helpToDeleteAccount(this, 'assessor');
    },

    checkIffocusOnProgressCircle(): boolean {
      const userHasntGotCompletionProfileTip =
        localStorage.getItem('userAlreadyGotCompletionProfileTip') !== 'true';
      const isNotTestUser = localStorage.getItem('isTestUser') !== 'true';

      return userHasntGotCompletionProfileTip && isNotTestUser;
    },

    checkIfShouldFocusOnContractDets(): void {
      if (store.state.lastVisitedProject) {
        const collapseCD = document.querySelector('.contract-details-collapse').firstChild
          .firstChild;

        this.focusUserAttentionOnCollapse(collapseCD);
      }
    },

    focusUserAttentionOnCollapse(collapseEl: {
      click: () => void;
      scrollIntoView: () => void;
    }): void {
      collapseEl.click();

      setTimeout(() => {
        collapseEl.scrollIntoView();
      }, 1500);
    },

    addRoleAssessorPlus(): void {
      this.upgradedToEazyPlus === true;
    },

    removeRoleAssessorPlus(): void {
      this.removedEazyPlus === true;
    },

    async submitNewsletterSubscription(): Promise<void> {
      const userId = store.state.userId;
      const subscribe = this.subscribedToNewsletter;

      const resp = await newsletter.updateSubscription(userId, subscribe);

      if (resp.ok) {
        this.$message.success(this.$t('yourSubscriptionStatusHasBeenUpdated'));
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
      }
    },
  },
};
